import React, { useState, useEffect } from 'react';
import { Check, Sparkles, Building2, Briefcase, Crown, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getFirestore, doc, updateDoc, getDoc } from 'firebase/firestore';
import { firebaseApp } from '../../firebase';
import toast from 'react-hot-toast';

const HROnboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const db = getFirestore(firebaseApp);

  // Check if user already h subscription
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user?.uid) return;

      const recruiterDoc = await getDoc(doc(db, 'recruiters', user.uid));
      const data = recruiterDoc.data();

      if (data?.subscriptionPlan && data?.isActive) {
        // Already h subscription, redirect to dashboard
        navigate('/hr');
      }
    };

    checkSubscription();
  }, [user, db, navigate]);

  const plans = [
    {
      name: 'Core',
      description: 'Perfect for small teams',
      icon: <Building2 className="w-8 h-8" />,
      pricing: {
        oneMonth: {
          original: 3999,
          discounted: 3999,
          discount: 0,
        },
        threeMonths: {
          original: 11997,
          discounted: 11397,
          discount: 5,
        },
        sixMonths: {
          original: 23994,
          discounted: 22794,
          discount: 5,
        },
        twelveMonths: {
          original: 47988,
          discounted: 45588,
          discount: 5,
        },
      },
      features: [
        '3 Job Posts',
        '150 Student Applications',
        'AI-powered test generation',
        'Candidate screening & shortlisting',
        'Advanced proctoring features',
        'Detailed candidate analytics',
      ],
      limitations: [
        'No custom branding',
        'No API access',
        'Standard support',
      ],
      highlighted: false,
    },
    {
      name: 'Nexora',
      description: 'For growing organizations',
      icon: <Briefcase className="w-8 h-8" />,
      pricing: {
        oneMonth: {
          original: 7999,
          discounted: 7999,
          discount: 0,
        },
        threeMonths: {
          original: 23997,
          discounted: 22797,
          discount: 5,
        },
        sixMonths: {
          original: 47994,
          discounted: 45594,
          discount: 5,
        },
        twelveMonths: {
          original: 95988,
          discounted: 91188,
          discount: 5,
        },
      },
      features: [
        '10 Job Posts',
        '500 Student Applications',
        'Advanced AI test generation',
        'Priority candidate screening',
        'Enhanced proctoring & security',
        'Advanced analytics & insights',
        'Custom branding',
        'Bulk candidate actions',
        'Email & chat support',
      ],
      highlighted: true,
    },
    {
      name: 'Enterprise',
      description: 'Custom solutions for large enterprises',
      icon: <Crown className="w-8 h-8" />,
      pricing: {
        oneMonth: {
          original: 0,
          discounted: 0,
          discount: 0,
        },
        threeMonths: {
          original: 0,
          discounted: 0,
          discount: 0,
        },
        sixMonths: {
          original: 0,
          discounted: 0,
          discount: 0,
        },
        twelveMonths: {
          original: 0,
          discounted: 0,
          discount: 0,
        },
      },
      features: [
        'Unlimited Job Posts',
        'Unlimited Applications',
        'Full AI capabilities',
        'White-label solution',
        'API access & integrations',
        'Custom test templates',
        'SSO & advanced security',
        'Dedicated account manager',
        'Custom SLA & support',
        'Advanced BI & reporting',
        'Multi-location support',
        'Custom training sessions',
      ],
      highlighted: false,
    },
  ];

  const getPrice = (plan, duration) => {
    switch (duration) {
      case 1:
        return plan.pricing.oneMonth;
      case 3:
        return plan.pricing.threeMonths;
      case 6:
        return plan.pricing.sixMonths;
      case 12:
        return plan.pricing.twelveMonths;
      default:
        return plan.pricing.oneMonth;
    }
  };

  const handlePayment = async (plan) => {
    if (plan.name === 'Enterprise') {
      toast.error('Please contact sales for Enterprise plan');
      return;
    }

    if (!user?.uid) {
      toast.error('Please login to continue');
      return;
    }

    setSelectedPlan(plan.name);
    setIsProcessing(true);

    try {
      const amount = getPrice(plan, selectedDuration);
      const amountInPaise = amount * 100;

      // Load Razorpay script
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: amountInPaise,
          currency: 'INR',
          name: 'VetriaAI',
          description: `${plan.name} Plan - ${selectedDuration} Month(s)`,
          image: '/VetriaAI.png',
          handler: async function (response) {
            await handlePaymentSuccess(response, plan, amount);
          },
          prefill: {
            name: user.displayName || '',
            email: user.email || '',
          },
          theme: {
            color: '#7C3AED',
          },
          modal: {
            ondismiss: function () {
              setIsProcessing(false);
              setSelectedPlan(null);
            },
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      };
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment initialization failed. Please try again.');
      setIsProcessing(false);
      setSelectedPlan(null);
    }
  };

  const handlePaymentSuccess = async (response, plan, amount) => {
    try {
      if (!user?.uid) return;

      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + selectedDuration);

      // Update recruiter document with subscription details
      await updateDoc(doc(db, 'recruiters', user.uid), {
        subscriptionPlan: plan.name,
        planDuration: selectedDuration,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        isActive: true,
        paymentId: response.razorpay_payment_id,
        orderId: response.razorpay_order_id,
        amountPaid: amount,
        lastPaymentDate: startDate.toISOString(),
        updatedAt: new Date().toISOString(),
      });

      toast.success('Payment successful! Welcome to VetriaAI');

      // Redirect to dashboard
      setTimeout(() => {
        navigate('/hr');
      }, 1500);
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Payment received but failed to update account. Please contact support.');
    } finally {
      setIsProcessing(false);
      setSelectedPlan(null);
    }
  };

  const renderPricingCard = (plan) => {
    const isSelected = selectedPlan === plan.name;
    const isEnterprise = plan.name === 'Enterprise';

    return (
      <div
        className={`relative rounded-2xl p-8 ${plan.highlighted
          ? 'bg-gradient-to-br from-purple-600 to-blue-600 text-white shadow-2xl scale-105'
          : 'bg-white text-gray-900 shadow-lg border border-gray-200'
          } transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 flex flex-col h-full`}
      >
        {plan.highlighted && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <div className="px-4 py-1 bg-yellow-400 text-purple-900 text-xs font-bold rounded-full flex items-center">
              <Sparkles className="w-3 h-3 mr-1" />
              MOST POPULAR
            </div>
          </div>
        )}

        <div className="text-center mb-6">
          <div
            className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${plan.highlighted ? 'bg-white/20' : 'bg-purple-100'
              }`}
          >
            <div className={plan.highlighted ? 'text-white' : 'text-purple-600'}>
              {plan.icon}
            </div>
          </div>
          <h3 className="text-3xl font-bold mb-2">{plan.name}</h3>
          <p
            className={`text-sm ${plan.highlighted ? 'text-purple-100' : 'text-gray-600'
              }`}
          >
            {plan.description}
          </p>
        </div>

        {/* Duration Selector */}
        {!isEnterprise && (
          <div className="mb-6">
            <label className={`block text-sm font-semibold mb-3 ${plan.highlighted ? 'text-purple-100' : 'text-gray-700'}`}>
              Select Duration:
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { months: 1, label: '1 Month' },
                { months: 3, label: '3 Months' },
                { months: 6, label: '6 Months' },
                { months: 12, label: '12 Months' },
              ].map(({ months, label }) => (
                <button
                  key={months}
                  onClick={() => setSelectedDuration(months)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${selectedDuration === months
                    ? plan.highlighted
                      ? 'bg-white text-purple-600'
                      : 'bg-purple-600 text-white'
                    : plan.highlighted
                      ? 'bg-white/20 text-white hover:bg-white/30'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pricing */}
        <div className="mb-6">
          {isEnterprise ? (
            <div className="text-center">
              <div className="flex items-baseline justify-center">
                <span className="text-5xl font-extrabold">Custom</span>
              </div>
              <p className={`text-sm mt-2 ${plan.highlighted ? 'text-purple-100' : 'text-gray-600'}`}>
                Contact sales for pricing
              </p>
            </div>
          ) : (
            <div className="text-center">
              <div className="flex items-baseline justify-center">
                <span className="text-5xl font-extrabold">
                  ₹{getPrice(plan, selectedDuration).toLocaleString()}
                </span>
              </div>
              <p className={`text-sm mt-2 ${plan.highlighted ? 'text-purple-100' : 'text-gray-600'}`}>
                for {selectedDuration} month{selectedDuration > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-3 mb-6 flex-grow">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <Check
                className={`w-5 h-5 mr-3 flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-yellow-300' : 'text-green-500'
                  }`}
              />
              <span className={`text-sm ${plan.highlighted ? 'text-purple-50' : 'text-gray-700'}`}>
                {feature}
              </span>
            </li>
          ))}
          {plan.limitations &&
            plan.limitations.map((limitation, index) => (
              <li key={`limit-${index}`} className="flex items-start">
                <X
                  className={`w-5 h-5 mr-3 flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-purple-300' : 'text-gray-400'
                    }`}
                />
                <span className={`text-sm ${plan.highlighted ? 'text-purple-200' : 'text-gray-500'}`}>
                  {limitation}
                </span>
              </li>
            ))}
        </ul>

        {/* CTA Button */}
        <button
          onClick={() => handlePayment(plan)}
          disabled={isProcessing && isSelected}
          className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${plan.highlighted
            ? 'bg-white text-purple-600 hover:bg-gray-100 shadow-lg'
            : 'bg-purple-600 text-white hover:bg-purple-700 hover:shadow-lg'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isProcessing && isSelected ? 'Processing...' : isEnterprise ? 'Contact Sales' : 'Get Started'}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center -space-x-[5px]">
            <img src="/VetriaAI.png" alt="VetriaAI Logo" className="w-10 h-10" />
            <span className="text-2xl font-bold text-gray-900">VetriaAI</span>
          </div>
          <div className="text-sm text-gray-600">
            Welcome, {user?.displayName || user?.email}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Select the perfect plan for your hiring needs. Start recruiting top talent with AI-powered assessments.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div key={index}>{renderPricingCard(plan)}</div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              All plans include AI-powered assessments and 24/7 support
            </p>
            <p className="text-sm text-gray-500">
              Prices are in Indian Rupees (INR). Secure payment powered by Razorpay.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HROnboarding;
